import { CopyOutlined, DeleteOutlined } from "@ant-design/icons";
import { App, Button, Dropdown, Flex, type MenuProps, Popconfirm } from "antd";
import { FC, SyntheticEvent, useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { OrderType, ProductOrderItemDetails } from "tm-shared/types";
import { buildRoutePath, formatUAHNarrow } from "tm-shared/utils";

import { ROUTES } from "@/constants";
import { IgnoreRemainingConfirm } from "@/domains/product";
import { useMultiTap } from "@/hooks";
import { useAppDispatch } from "@/hooks/redux";
import { PrimaryText, SecondaryText } from "@/shared/ui/Typography";
import { addIgnoreRemainingOrder } from "@/store/slices/offline/offline.slice";
import { copyObjectToClipboard } from "@/utils/dom/copyObjectToClipboard";
import { handleFocus } from "@/utils/dom/handleFocus";

import { ORDER_TYPES_WITH_DISABLED_CHANGES } from "../..";
import {
    StyledBarcodeButton,
    StyledMoreOutlined,
    StyledODNumberInput,
    StyledProductRow,
    StyledRowBottom,
} from "./Styled";

interface ProductRowProps {
    item: ProductOrderItemDetails;
    isClosed: boolean;
    inventoryCount: number;
    validationErrors: Record<string, boolean>;
    orderId: string;
    onCountChange: (
        item: ProductOrderItemDetails,
        newCount: number | null,
    ) => void;
    onInputBlur: (item: ProductOrderItemDetails) => void;
    onDelete: (item: string) => void;
    orderType?: OrderType;
    loading: boolean;
    ignoreRemaining: {
        product: boolean;
        order: boolean;
    };
}

export const ProductRow: FC<ProductRowProps> = ({
    item,
    isClosed,
    inventoryCount,
    validationErrors,
    orderId,
    onCountChange,
    onInputBlur,
    onDelete,
    orderType,
    loading,
    ignoreRemaining,
}) => {
    const { notification } = App.useApp();

    const [isModalOpened, setIsModalOpened] = useState(false);
    const [showIgnoreWrapper, setShowIgnoreWrapper] = useState(false);
    const dispatch = useAppDispatch();

    const barcode = item.barcode;
    const isError = !!validationErrors[barcode];
    const totalPrice = item.price * item.count;

    const isIgnoreRemaining = ignoreRemaining.product;
    const disabledEditing =
        isClosed ||
        (orderType && ORDER_TYPES_WITH_DISABLED_CHANGES.includes(orderType)) ||
        loading;

    const dropdownMenu: MenuProps = {
        items: [
            {
                key: "delete",
                label: (
                    <Popconfirm
                        title="Видалити?"
                        description="Точно видалити товар?"
                        okText="Так"
                        cancelText="Ні"
                        onConfirm={() => onDelete(barcode)}
                    >
                        <Flex gap={8} align="center">
                            <DeleteOutlined /> Видалити
                        </Flex>
                    </Popconfirm>
                ),
            },
        ],
    };

    const confirmIgnoreRemaining = useCallback(() => {
        dispatch(addIgnoreRemainingOrder({ orderId, barcode }));
        setIsModalOpened(false);
    }, [dispatch, orderId, barcode]);

    const handleIgnoreRemaining = useCallback(() => {
        if (ignoreRemaining.order) {
            dispatch(addIgnoreRemainingOrder({ orderId, barcode }));
        } else {
            setIsModalOpened(true);
        }
    }, [dispatch, ignoreRemaining.order, orderId, barcode]);

    const handleTap = useMultiTap(() => {
        if (isIgnoreRemaining || disabledEditing) return;

        setShowIgnoreWrapper(true);
    });

    const handleCopyProductInfo = useCallback(
        (e: SyntheticEvent) => {
            e.stopPropagation();
            copyObjectToClipboard(
                {
                    barcode,
                    name: item.name,
                    orderId,
                    price: item.price,
                    itemsLeft: inventoryCount,
                },
                notification,
            );
        },
        [notification, barcode, item.name, item.price, orderId, inventoryCount],
    );

    return (
        <StyledProductRow>
            <IgnoreRemainingConfirm
                open={isModalOpened}
                onConfirm={confirmIgnoreRemaining}
                onClose={() => setIsModalOpened(false)}
            />

            <Flex justify="space-between" gap={12} align="flex-start">
                <Flex vertical gap={4} align="flex-start">
                    <Link to={buildRoutePath(ROUTES.ProductPage, { barcode })}>
                        <SecondaryText fontWeight="500" lineHeight="120%">
                            {item.name}
                        </SecondaryText>
                    </Link>

                    <StyledBarcodeButton
                        type="text"
                        size="small"
                        onClick={handleCopyProductInfo}
                    >
                        <Flex gap={8}>
                            <SecondaryText>Код:</SecondaryText>
                            <SecondaryText fontWeight="500">
                                {barcode} <CopyOutlined />
                            </SecondaryText>
                        </Flex>
                    </StyledBarcodeButton>

                    {orderType !== OrderType.R && (
                        <Flex gap={8}>
                            <SecondaryText>Залишок:</SecondaryText>
                            <SecondaryText fontWeight="500">
                                {inventoryCount}
                            </SecondaryText>
                        </Flex>
                    )}
                </Flex>

                {!disabledEditing && (
                    <Dropdown menu={dropdownMenu}>
                        <StyledMoreOutlined />
                    </Dropdown>
                )}
            </Flex>

            {orderType !== OrderType.R ? (
                <StyledRowBottom>
                    <PrimaryText textColor="accent" textAlign="center">
                        {formatUAHNarrow(item.price)}
                    </PrimaryText>

                    <Flex justify={"center"} onTouchStart={handleTap}>
                        <StyledODNumberInput
                            min={0}
                            onFocus={handleFocus}
                            value={item.count}
                            inputMode="numeric"
                            disabled={disabledEditing}
                            parser={(val = "") => {
                                const parsed = parseInt(
                                    val.replace(/\D/g, ""),
                                    10,
                                );
                                return isNaN(parsed) ? 0 : parsed;
                            }}
                            formatter={(val) =>
                                (val ?? "").toString().replace(/\D/g, "")
                            }
                            isError={isError}
                            onChange={(val) => {
                                const number =
                                    val === null
                                        ? 0
                                        : typeof val === "number"
                                          ? val
                                          : parseInt(val, 10);
                                onCountChange(item, isNaN(number) ? 0 : number);
                            }}
                            onBlur={() => onInputBlur(item)}
                        />
                    </Flex>

                    <PrimaryText textColor="accent" textAlign="center">
                        {formatUAHNarrow(totalPrice)}
                    </PrimaryText>
                </StyledRowBottom>
            ) : (
                <SecondaryText fontWeight="500">
                    Кількість: {item.count}
                </SecondaryText>
            )}

            {showIgnoreWrapper && !isIgnoreRemaining && (
                <Flex justify="center">
                    <Button type="primary" onClick={handleIgnoreRemaining}>
                        Ігнорувати залишки
                    </Button>
                </Flex>
            )}
        </StyledProductRow>
    );
};
